import React, { ChangeEvent, FormEvent, useState } from "react"
import { Button, Dialog, Grid, Typography } from "@material-ui/core"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import { useIntl } from "react-intl"
import { GAEvent } from "../../../modules/GoogleAnalytics"
import usePlatform from "../../../hooks/usePlatform"
import { useTrack } from "@hackler/react-sdk"
import { DialogContent, DialogTitle } from "../../../components/Dialog/DemoRequestFormDialog"
import { requestDemo } from "../../../apis/requestDemoApi"
import PricingCardsNew from "../elements/PricingCardsNew"
import { Locale } from "../../../locale"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      padding: "40px 0px",
      background: "#FFF",
      [theme.breakpoints.up("md")]: {
        padding: "80px 20px",
      },
    },
    description: {
      marginTop: theme.spacing(1),
      fontSize: "16px",
      marginBottom: 15,
      [theme.breakpoints.up("md")]: {
        fontSize: "24px",
        marginBottom: 57,
      },
      "& > span ": {
        textAlign: "center",
      },
    },
    primaryColor: {
      color: theme.palette.primary.main,
    },
    input: {
      padding: "10px 10px",
      fontSize: "12px",
      width: "100%",
      border: "1px solid #EDEDED",
      "&::placeholder": {
        color: "rgba(0, 0, 0, 0.3)",
      },
      [theme.breakpoints.up("md")]: {
        padding: "12px 20px",
        fontSize: "16px",
      },
    },
    textArea: {
      width: "100%",
      height: "84px",
      boxSizing: "border-box",
      fontSize: "12px",
      padding: "10px 10px",
      border: "1px solid #EDEDED",
      resize: "none",
      "&::placeholder": {
        color: "rgba(0, 0, 0, 0.3)",
      },
      [theme.breakpoints.up("md")]: {
        padding: "12px 20px",
        height: "144px",
        fontSize: "16px",
      },
    },
    btn: {
      height: "40px",
      fontSize: "16px",
      textTransform: "none",

      [theme.breakpoints.up("md")]: {
        height: "60px",
        fontSize: "20px",
      },
    },
  })
)

export default function Pricing() {
  const intl = useIntl()
  const track = useTrack()
  const classes = useStyles()
  const { isMobile } = usePlatform()
  const [opened, setOpened] = useState(false)
  const [formData, setFormData] = useState({ type: "ENTERPRISE_PLAN", email: "", name: "", contact: "", comment: "" })
  const { lang } = Locale.getDefault()

  const handleOpen = () => {
    GAEvent({
      category: "pricing",
      action: "click",
      label: "inquiry enterprise plan",
    })
    track({ key: "homepage_pricing_compare_main_enterprise_plan_question_click" })
    setOpened(true)
  }
  const inquire = () => setOpened(true)
  const handleClose = () => setOpened(false)

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value
    setFormData({
      ...formData,
      [event.target.name]: value,
    })
  }

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const requestParam = { ...formData, comment: `${formData.comment}` }
    if (await requestDemo(requestParam)) {
      alert(intl.formatMessage({ id: "REQUESTED_DEMO_DESC1" }) + intl.formatMessage({ id: "REQUESTED_DEMO_DESC2" }))
      handleClose()
    }
  }

  return (
    <div className={classes.root}>
      <Grid container justify={"center"}>
        <Grid item xs={12}>
          {lang === "ko" ? (
            <>
              <Typography variant={"h2"}>
                <strong className={classes.primaryColor}>스타트업 무료 플랜</strong>을 통해 {isMobile && <br />} 부담
                없이 시작하세요
              </Typography>
              <Typography variant={"h3"} className={classes.description}>
                초기 스타트업부터 글로벌 IT 기업까지, {isMobile && <br />} 실험을 통해 고객 경험을 개선할 수 있습니다.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant={"h2"}>
                Get started with our
                {isMobile && <br />} <strong className={classes.primaryColor}>Free Startup Plan</strong>
              </Typography>
              <Typography variant={"h3"} className={classes.description}>
                Whether you are a startup or {isMobile && <br />}a global IT business, {isMobile && <br />}improve
                consumer experiences.
              </Typography>
            </>
          )}
        </Grid>
        <Grid item xs={10} style={{ maxWidth: isMobile ? 375 : 1198 }}>
          <PricingCardsNew handleOpen={handleOpen} inquire={inquire} />
        </Grid>
      </Grid>

      <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={opened}>
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          <strong>{lang === "ko" ? "요금제 문의하기" : "Contact us"}</strong>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <form onSubmit={onSubmit}>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={12}>
                    <input
                      required
                      type={"email"}
                      name={"email"}
                      className={classes.input}
                      placeholder={intl.formatMessage({ id: "REQUIRED_EMAIL" })}
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      name={"name"}
                      className={classes.input}
                      placeholder={intl.formatMessage({ id: "NAME" })}
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      name={"contact"}
                      className={classes.input}
                      placeholder={intl.formatMessage({ id: "PHONE_NUMBER" })}
                      value={formData.contact}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <textarea
                      name={"comment"}
                      className={classes.textArea}
                      onChange={handleInputChange}
                      value={formData.comment}
                      placeholder={intl.formatMessage({ id: "REQUEST_DEMO_COMMENT" })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type={"submit"}
                      color={"primary"}
                      variant={"contained"}
                      fullWidth
                      className={classes.btn}
                      onClick={() => {
                        GAEvent({
                          category: "demo dialog",
                          action: "click",
                          label: "request demo",
                        })
                        track({ key: "homepage_home_request_enterprise_question_click" })
                      }}>
                      <strong>{intl.formatMessage({ id: "inquire" })}</strong>
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  )
}
